import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Slide, Stack, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useEffectiveTenant } from '../../hooks/useEffectiveTenant';
import { useAppSelector } from '../../hooks/useAppStore';

/**
 * INVITACIÓN A INSTALAR "IMPORTAR A IMPERIUM". (08/10/2026; tarjeta en vez de modal, 09/10/2026)
 *
 * Una NOTIFICACIÓN, no un modal (pedido del user): arriba a la derecha, en cualquier pantalla
 * del CRM, sin tapar nada. Queda hasta que el agente la cierra con la ✕, y cerrada no vuelve
 * (por usuario, en este navegador).
 *
 * Chrome no deja instalar una extensión desde una web (desde 2018 solo por la Chrome Web Store):
 * el botón lleva a la ficha, donde es un clic. Solo en Chrome de computadora y solo si la
 * extensión NO está: su script corre en el CRM al cargar y marca `data-imperium-extension` en el
 * <html>. Si la marca llega después de pintar la tarjeta, la tarjeta se va sola.
 *
 * Sin `VITE_EXTENSION_MELI_URL` no aparece. Solo a usuarios de Imperium (pedido del user).
 */
const URL_STORE = import.meta.env.VITE_EXTENSION_MELI_URL as string | undefined;
const claveCerrada = (userId: string) => `imperium:extension-meli:cerrada:${userId}`;

function esChromeDeEscritorio() {
  const ua = navigator.userAgent;
  return /Chrome\//.test(ua) && !/Edg\/|OPR\/|Mobile|Android|CriOS/.test(ua);
}

const extensionInstalada = () => !!document.documentElement.dataset.imperiumExtension;

function cerrada(userId: string) {
  try { return localStorage.getItem(claveCerrada(userId)) === '1'; } catch { return false; }
}

export function InstalarExtensionAviso() {
  const [visible, setVisible] = useState(false);
  const { tenantMode } = useEffectiveTenant();
  const userId = useAppSelector((s) => s.auth.user?._id);
  const esImperium = tenantMode === 'native';

  useEffect(() => {
    if (!URL_STORE || !esImperium || !userId || !esChromeDeEscritorio() || cerrada(userId)) return;
    // Un momento después de entrar: le da tiempo al script de la extensión a marcar el <html>.
    const t = setTimeout(() => { if (!extensionInstalada()) setVisible(true); }, 1500);
    // Y si la marca llega más tarde, se esconde sola.
    const obs = new MutationObserver(() => { if (extensionInstalada()) setVisible(false); });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-imperium-extension'] });
    return () => { clearTimeout(t); obs.disconnect(); };
  }, [esImperium, userId]);

  const cerrar = () => {
    if (userId) { try { localStorage.setItem(claveCerrada(userId), '1'); } catch { /* sin storage: vuelve a aparecer */ } }
    setVisible(false);
  };

  if (!URL_STORE || !esImperium) return null;

  return (
    <Slide direction="left" in={visible} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        role="status"
        sx={{
          position: 'fixed', top: 72, right: 20, zIndex: (t) => t.zIndex.snackbar,
          width: 360, maxWidth: 'calc(100vw - 32px)', p: 2, pr: 1.5, borderRadius: 3,
          border: 1, borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box component="img" src="/extension-mercadolibre/icono.png" alt="" sx={{ width: 44, height: 44, borderRadius: 2, flexShrink: 0 }} />
          <Box flex={1} minWidth={0}>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 1.5, lineHeight: 1.4, display: 'block' }}>
              Nuevo · ya disponible
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>Importá propiedades de MercadoLibre</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Con la extensión de Chrome, la propiedad de MercadoLibre que estás mirando entra al CRM <b>en borrador</b> o
              como <b>captación</b>, con fotos y datos.
            </Typography>
            <Button variant="contained" size="small" href={URL_STORE} target="_blank" rel="noopener" sx={{ mt: 1.5 }}>
              Agregar a Chrome
            </Button>
          </Box>
          <IconButton size="small" onClick={cerrar} aria-label="Cerrar" sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>
    </Slide>
  );
}
